import html2canvas from 'html2canvas'
import { FastAverageColor } from 'fast-average-color'
import 'requestidlecallback-polyfill'

import { throttle, debounce } from './helpers'

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

// Safari wont read colors from a scaled canvas
const SCALE = isSafari ? 1 : 0.25
const CLASSNAME_LIGHT = 'is-light'

const HeaderColor = function () {

	const elements = {}
	let fac, isSanpping, snapStart = 0, snapEnd = 0, debug = false

	const init = function () {
		elements.header = document.querySelector('.header')
		elements.canvas = undefined

		if (elements.header) {
			fac = new FastAverageColor()
			setStartEnd()

			// Use resize observer to snap the body after resizing the page
			const resizeObserver = new ResizeObserver(debouncedSnapBodyToCanvas)
			resizeObserver.observe(document.body)

			// On load, snap the body to the canvas and set the page theme
			window.addEventListener('load', snapBodyToCanvas)

			// Set the page theme on scroll
			document.addEventListener('scroll', throttledSetPageTheme)
			document.addEventListener('scroll', setStartEnd)
		}
	}

	const debouncedSnapBodyToCanvas = debounce(snapBodyToCanvas, 1000)
	const throttledSetPageTheme = throttle(setPageTheme, 10)

	let lastScrollY = window.scrollY - 1

	function setStartEnd() {
		const min = 0
		const max = document.body.scrollHeight

		const height = window.innerHeight
		const scrollY = Math.floor(window.scrollY)

		let newSnapStart = Math.max(min, scrollY - (height * (scrollY > lastScrollY ? 1.5 : 4)))
		let newSnapEnd = Math.min(max, scrollY + (height * (scrollY > lastScrollY ? 4 : 1.5)))

		if (
			(scrollY < lastScrollY && newSnapStart < snapStart && scrollY < snapStart + height) ||
			(scrollY > lastScrollY && newSnapEnd > snapEnd && scrollY > snapEnd - height)
		) {
			snapStart = newSnapStart
			snapEnd = newSnapEnd

			requestIdleCallback(snapBodyToCanvas)

			if (debug) {
				console.log('setStartEnd', { snapStart, snapEnd })
			}
		}

		lastScrollY = scrollY
	}

	function snapBodyToCanvas() {
		isSanpping = true

		if (debug) {
			console.time('snapBodyToCanvas')
		}

		try {
			html2canvas(document.body, {
				debug,
				scale: SCALE,
				y: snapStart,
				scrollY: snapStart,
				height: snapEnd - snapStart,
				ignoreElements: element => {
					return element === elements.header ||
						element.tagName === 'VIDEO' ||
						element.tagName === 'IFRAME' ||
						element.classList.contains('flyout')
				},
				onclone: (clonedElement) => {
					clonedElement.getElementById('debug-html2canvas')?.remove()
				}
			}).then(canvas => {
				canvas.id = 'debug-html2canvas'
				Object.assign(canvas.style, {
					pointerEvents: 'none',
					position: 'absolute',
					zIndex: 2,
					top: snapStart + 'px',
					left: 0,
					opacity: .75,
				})

				if (debug) {
					if (elements.canvas) {
						elements.canvas.parentNode?.replaceChild(canvas, elements.canvas)
					} else {
						document.body.appendChild(canvas)
					}

					console.timeEnd('snapBodyToCanvas')
				} else {
					elements.canvas?.parentNode?.removeChild(elements.canvas)
				}

				elements.canvas = canvas
				isSanpping = false

				setPageTheme()
			})
		} catch (error) {
			if (debug) {
				console.error(error)
			}
		}
	}

	let colorThemeTimeout = undefined
	let currentColorTheme = elements.header?.classList.contains(CLASSNAME_LIGHT)

	function setPageTheme() {
		if (!elements.canvas || isSanpping) return

		const scrollTop = (window.scrollY - snapStart) * SCALE
		const headerTop = elements.header.offsetTop * SCALE
		const headerWidth = elements.header.offsetWidth * SCALE
		const headerHeight = elements.header.offsetHeight * SCALE

		if (scrollTop + headerTop < 0) {
			return
		}

		if (debug) {
			console.time('fastAverageColor')
			console.log('get dominant color from area:', {
				top: scrollTop + headerTop,
				width: headerWidth,
				height: headerHeight
			})
		}

		const color = fac.getColor(elements.canvas, {
			ignoredColor: [0, 0, 0, 255],
			algorithm: 'dominant',
			step: 25,
			left: 0,
			top: scrollTop + headerTop,
			width: headerWidth,
			height: headerHeight
		})

		if (debug) {
			console.timeEnd('fastAverageColor')
			console.log('%c  ', `background:${color.hex}`, color.hex, 'isLight:', color.isLight)
		}

		// Apply color theme after a timeout to avoid flickering
		if (color.isLight !== currentColorTheme) {
			clearTimeout(colorThemeTimeout)

			if (debug) {
				console.log('colorThemeTimeout celared')
			}

			currentColorTheme = color.isLight
			colorThemeTimeout = setTimeout(() => {
				if (debug) {
					console.log('colorThemeTimeout fired')
				}

				elements.header.classList.toggle(CLASSNAME_LIGHT, color.isLight)
			}, 150)
		}
	}

	return {
		init,
		set debug(value) {
			debug = !!value
			if (elements.canvas) {
				if (!debug) {
					elements.canvas.parentNode?.removeChild(elements.canvas)
				} else {
					document.body.appendChild(elements.canvas)
				}
			}
		}
	}
}()

export default HeaderColor
